// src/components/RequireAuth.tsx
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface RequireAuthProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

const RequireAuth: React.FC<RequireAuthProps> = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isWaitingForSession, setIsWaitingForSession] = useState(false);

  console.log("RequireAuth: user =", user, "loading =", loading, "path =", location.pathname); // Debug log
  
  // Additional debugging for authentication flow
  if (process.env.NODE_ENV === 'development') {
    console.log("RequireAuth debug:", {
      hasUser: !!user,
      userEmail: user?.email,
      userFirstName: user?.firstName,
      loading,
      pathname: location.pathname,
      timestamp: new Date().toISOString(),
      hasLocalStorageData: !!(localStorage.getItem('authToken') && localStorage.getItem('authUser'))
    });
  }

  // Check if we have localStorage data that indicates a session exists
  const [hasLocalStorageData, setHasLocalStorageData] = useState(false);
  
  // Effect to check localStorage data with retry logic
  useEffect(() => {
    const checkLocalStorage = () => {
      try {
        const token = localStorage.getItem('authToken');
        const user = localStorage.getItem('authUser');
        const hasData = !!(token && user);
        setHasLocalStorageData(hasData);
        
        if (hasData) {
          console.log("RequireAuth: LocalStorage data found:", {
            hasToken: !!token,
            hasUser: !!user,
            tokenLength: token?.length || 0,
            userLength: user?.length || 0
          });
        }
      } catch (error) {
        console.warn("RequireAuth: LocalStorage not available yet, retrying...", error);
        // Retry after a short delay
        setTimeout(checkLocalStorage, 100);
      }
    };
    
    checkLocalStorage();
  }, []);
  
  // More robust page refresh detection
  const isPageRefresh = (() => {
    // Method 1: Performance Navigation API
    if ((performance as any).navigation?.type === 1) {
      return true;
    }
    
    // Method 2: Performance Entries
    const entries = performance.getEntriesByType('navigation');
    if (entries.length > 0 && (entries[0] as any)?.type === 'reload') {
      return true;
    }
    
    // Method 3: Check if this is the first load of the page
    const isFirstLoad = !sessionStorage.getItem('hasLoadedBefore');
    if (isFirstLoad) {
      sessionStorage.setItem('hasLoadedBefore', 'true');
      return false;
    }
    
    // Method 4: Check if we have localStorage data but no user in context (likely a refresh)
    if (hasLocalStorageData && !user && !loading) {
      return true;
    }
    
    return false;
  })();
  
  console.log("RequireAuth state check:", {
    hasUser: !!user,
    loading,
    hasLocalStorageData,
    isPageRefresh,
    pathname: location.pathname,
    isWaitingForSession,
    performanceNavigation: (performance as any).navigation?.type,
    performanceEntries: performance.getEntriesByType('navigation').map((e: any) => e.type),
    hasLoadedBefore: sessionStorage.getItem('hasLoadedBefore')
  });

  // Effect to handle session restoration waiting
  useEffect(() => {
    if (hasLocalStorageData && !user && !loading) {
      console.log("RequireAuth: Setting up session restoration wait timer");
      setIsWaitingForSession(true);
      
      // Wait for session restoration to complete
      const timer = setTimeout(() => {
        console.log("RequireAuth: Session restoration wait timer expired");
        setIsWaitingForSession(false);
      }, 1500); // Reduced to 1.5 seconds for better UX
      
      return () => clearTimeout(timer);
    } else if (user || !hasLocalStorageData) {
      setIsWaitingForSession(false);
    }
  }, [hasLocalStorageData, user, loading]);

  // Debug effect to track state changes
  useEffect(() => {
    console.log("RequireAuth state changed:", {
      user: !!user,
      loading,
      hasLocalStorageData,
      isPageRefresh,
      isWaitingForSession,
      pathname: location.pathname,
      timestamp: new Date().toISOString()
    });
  }, [user, loading, hasLocalStorageData, isPageRefresh, isWaitingForSession, location.pathname]);
  
  // Show loading state while authentication is initializing
  // Only show loading if we're truly initializing and have no session data
  // This prevents unnecessary loading on page refresh when we have localStorage data
  const shouldShowLoading = loading && !user && !hasLocalStorageData;
  
  if (shouldShowLoading) {
    console.log("RequireAuth: Showing loading state - initializing without session data");
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div>Authenticating...</div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // If we're still loading but have localStorage data, wait a bit more
  // This gives the AuthContext time to restore the session
  if (loading && hasLocalStorageData) {
    console.log("RequireAuth: Showing session restoration loading - waiting for session to be restored");
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div>Restoring session...</div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // CRITICAL FIX: If we have localStorage data but no user in context yet, 
  // we should wait for the session restoration to complete
  // This prevents the race condition that causes premature redirects
  if (!user && hasLocalStorageData && (isWaitingForSession || isPageRefresh)) {
    console.log("RequireAuth: Has localStorage data but no user in context yet - waiting for session restoration");
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div>Finalizing session restoration...</div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // If there's no authenticated user and we're not loading, redirect to login
  // Only redirect if we have no localStorage data and are not waiting for session restoration
  if (!user && !loading && !hasLocalStorageData && !isWaitingForSession) {
    console.log("RequireAuth: No user found, not loading, and no localStorage data - redirecting to login");
    // Only redirect if we're not already on an auth page
    const currentPath = location.pathname;
    if (!currentPath.includes('/auth/')) {
      return <Navigate to="/auth/login" state={{ from: location }} replace />;
    }
    return null;
  }

  // If allowedRoles is provided, check if user has one of the allowed roles
  if (user && allowedRoles && (!user.role || !allowedRoles.includes(user.role))) {
    console.log("RequireAuth: User role not allowed", {
      userRole: user.role,
      allowedRoles,
      userData: {
        firstName: user.firstName,
        email: user.email,
        id: user.id || user.userId
      }
    });
    // Redirect to NotAuthorized page
    return <Navigate to="/not-authorized" replace />;
  }

  console.log("RequireAuth: User authenticated successfully", {
    userRole: user?.role,
    pathname: location.pathname,
    allowedRoles: allowedRoles || 'any'
  });
  return children;
};

export default RequireAuth;
